import { useState } from "react";
import { HelpCircle, Link2, MessageCircle, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Guía del aviso de jornada por Telegram (sin plantillas Meta/Altiria).
 * El bot envía texto + botones URL a la misma mini-web que WhatsApp.
 */
export default function GuiaFichajeTelegram({ triggerClassName = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5 border-sky-200 text-sky-800 ${triggerClassName}`}
          data-testid="fichaje-telegram-guia-btn"
        >
          <HelpCircle className="h-4 w-4" />
          Guía
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Jornada por Telegram — Ventura HR</DialogTitle>
          <DialogDescription>
            En Telegram no hace falta plantilla de Meta ni Altiria: el bot envía el mensaje
            directamente con botones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-zinc-700">
          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2.5 text-xs text-sky-950 space-y-1.5">
            <p className="font-medium">Entrada y salida con el mismo aviso</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                Un solo enlace sirve para <strong>entrada</strong> y, más tarde, para{" "}
                <strong>salida</strong> (según lo que falte ese día).
              </li>
              <li>
                <strong>Registrar</strong> — picado a la hora prevista del horario.
              </li>
              <li>
                <strong>Modificar hora</strong> — el trabajador elige la hora a mano.
              </li>
            </ul>
          </div>

          <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
            <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div className="text-xs space-y-1.5">
              <p className="font-medium text-sky-950">1. Mensaje del bot (automático)</p>
                    <p className="text-zinc-600">
                      Ventura envía el aviso <strong>minutos después</strong> de la hora prevista del
                      picado pendiente (entrada o salida). Ejemplo: entrada 09:00 + 15 min → desde 09:15.
                    </p>
              <pre className="mt-1 whitespace-pre-wrap rounded-md bg-zinc-50 p-2 font-mono text-[11px] text-zinc-700">
                {`Hola Pedro,

Registra tu jornada del 05/09/2026
(horario previsto 09:00 a 17:00).

Puedes registrar la entrada o salida
que corresponda, o modificar la hora.`}
              </pre>
            </div>
          </div>

          <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
            <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div className="text-xs space-y-1.5">
              <p className="font-medium text-sky-950">2. Botones inline (como plantilla WA)</p>
              <ul className="list-disc pl-4 space-y-1 text-zinc-600">
                <li>
                  <strong>Registrar jornada</strong> → abre la mini-web con{" "}
                  <code className="rounded bg-zinc-100 px-1">?accion=registrar</code>
                </li>
                <li>
                  <strong>Modificar hora</strong> →{" "}
                  <code className="rounded bg-zinc-100 px-1">?accion=modificar</code>
                </li>
              </ul>
              <p className="text-zinc-600">
                La mini-web es la misma que WhatsApp:{" "}
                <code className="rounded bg-zinc-100 px-1">/wa/jornada/TOKEN</code>
              </p>
            </div>
          </div>

          <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
            <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div className="text-xs space-y-1">
              <p className="font-medium text-sky-950">3. Requisitos</p>
              <ul className="list-disc pl-4 space-y-1 text-zinc-600">
                <li>Bot activo en Ajustes → Telegram (token + username).</li>
                <li>Trabajador vinculado (Empleados → Enviar vínculo).</li>
                <li>
                  URL pública de la app (campo de WhatsApp o{" "}
                  <code className="rounded bg-zinc-100 px-1">http://localhost:8083</code> en
                  local): los botones de Telegram exigen enlace http/https completo.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
